import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LEAD_COLLECTION_SOURCE_OPTIONS,
  LEAD_CONSENT_STATUS_OPTIONS,
  LEAD_LAWFUL_BASIS_OPTIONS,
  LEAD_PRIVACY_NOTICE_STATUS_OPTIONS,
  LEAD_SORTABLE_COLUMNS,
  LEAD_STATUS_OPTIONS,
} from '../../../config/enum-options.js';
import {
  FilterPresetList,
  FilterPresetNameModal,
  FilterPresetSaveButton,
} from '../../../shared/filter-presets/filter-presets.jsx';
import AssignmentModal from '../../../shared/ui/assignment-modal/assignment-modal.jsx';
import FloatingAlerts from '../../../shared/ui/floating-alerts/floating-alerts.jsx';
import { getEnumOptionLabel } from '../../../shared/i18n/enum-labels.js';
import LeadForm from './lead-form.jsx';

function getStatusBadgeClass(statusValue) {
  const status = String(statusValue || '').trim().toLowerCase();
  const map = {
    new: 'bg-info-subtle text-info',
    contacted: 'bg-primary-subtle text-primary',
    engaged: 'bg-primary-subtle text-primary',
    qualified: 'bg-success-subtle text-success',
    scheduled: 'bg-success-subtle text-success',
    converted: 'bg-success-subtle text-success',
    nurturing: 'bg-warning-subtle text-warning',
    unresponsive: 'bg-secondary-subtle text-secondary',
    duplicate: 'bg-secondary-subtle text-secondary',
    rejected: 'bg-danger-subtle text-danger',
    'not-interested': 'bg-danger-subtle text-danger',
    'do-not-contact': 'bg-danger-subtle text-danger',
  };

  return map[status] || 'bg-secondary-subtle text-secondary';
}

function getGdprBadgeClass(kind, value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (kind === 'lawfulBasis') {
    return normalized === 'unknown'
      ? 'bg-danger-subtle text-danger'
      : 'bg-primary-subtle text-primary';
  }
  if (kind === 'consentStatus') {
    return normalized === 'given'
      ? 'bg-success-subtle text-success'
      : 'bg-warning-subtle text-warning';
  }
  if (kind === 'privacyNoticeStatus') {
    return ['sent', 'acknowledged', 'not_required'].includes(normalized)
      ? 'bg-success-subtle text-success'
      : 'bg-warning-subtle text-warning';
  }

  return 'bg-secondary-subtle text-secondary';
}

function buildMatchedProfilePath(row) {
  const path = String(row?.matchedProfilePath || '').trim();
  if (path) {
    return path;
  }

  const uid = String(row?.matchedProfileUid || '').trim();
  return uid ? `/profiles/${encodeURIComponent(uid)}` : '';
}

function getMatchedProfileLabel(row) {
  const name = String(row?.matchedProfileName || '').trim();
  const maskedEmail = String(row?.matchedProfileEmailMasked || '').trim();
  if (name && !(name.includes('@') && name.includes('.'))) {
    return name;
  }

  return maskedEmail || '';
}

function renderOptions(options, t, namespace) {
  return options.map((value) => (
    <option key={value} value={value}>
      {getEnumOptionLabel(t, namespace, value)}
    </option>
  ));
}

function LeadFormModal({
  isOpen,
  target,
  form,
  formErrors,
  error,
  isSaving,
  isLoadingDetails,
  countryOptions,
  onChange,
  onAddDetail,
  onRemoveDetail,
  onSubmit,
  onClose,
}) {
  const { t } = useTranslation();
  if (!isOpen) {
    return null;
  }

  const title = target?.id ? t('leads.editLeadTitle') : t('leads.createLeadTitle');

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={isSaving}></button>
            </div>
            <LeadForm
              form={form}
              formErrors={formErrors}
              error={error}
              isSaving={isSaving}
              isLoadingDetails={isLoadingDetails}
              countryOptions={countryOptions}
              onChange={onChange}
              onAddDetail={onAddDetail}
              onRemoveDetail={onRemoveDetail}
              onSubmit={onSubmit}
              onCancel={onClose}
              bodyClassName="modal-body"
              actionsClassName="modal-footer"
            />
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

function LeadDeleteModal({
  isOpen,
  target,
  reason,
  error,
  isDeleting,
  onReasonChange,
  onConfirm,
  onClose,
}) {
  const { t } = useTranslation();
  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t('leads.deleteTitle')}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={isDeleting}></button>
            </div>
            <div className="modal-body">
              {error ? <div className="alert alert-danger py-2">{error}</div> : null}
              <p>{t('leads.deleteConfirm')} <strong>{target?.name || '-'}</strong></p>
              <label className="form-label">{t('leads.deleteReason')}</label>
              <textarea
                className={`form-control ${error ? 'is-invalid' : ''}`}
                name="deleteReason"
                value={reason}
                onChange={(event) => onReasonChange(event.target.value)}
                rows="3"
                maxLength="500"
              ></textarea>
              <div className="form-text">{t('leads.deleteReasonHelp')}</div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" onClick={onClose} disabled={isDeleting}>{t('common.cancel')}</button>
              <button type="button" className="btn btn-danger" onClick={onConfirm} disabled={isDeleting}>
                {isDeleting ? t('leads.deleting') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

function LeadsPageView({
  canImport = false,
  rows,
  countryOptions,
  filters,
  isFilterOpen,
  isLoading,
  isLoadingMore,
  hasMoreLeads,
  error,
  isAssignModalOpen,
  assignTargetLeadName,
  adminProfileOptions,
  isAdminProfilesLoading,
  selectedManagerId,
  isAssignmentSaving,
  assignmentError,
  toggleSort,
  updateFilter,
  resetAndCloseFilters,
  setIsFilterOpen,
  openAssignModal,
  closeAssignModal,
  onSelectManager,
  assignManager,
  unassignManager,
  sortIndicator,
  loadMoreLeads,
  isLeadFormOpen,
  leadFormTarget,
  leadForm,
  leadFormErrors,
  leadFormError,
  isLeadFormSaving,
  isLeadFormLoadingDetails,
  onLeadFormChange,
  addLeadDetailRow,
  removeLeadDetailRow,
  saveLeadForm,
  openLeadForm,
  closeLeadForm,
  isDeleteModalOpen,
  deleteTargetLead,
  deleteReason,
  deleteError,
  isDeletingLead,
  openDeleteModal,
  closeDeleteModal,
  onDeleteReasonChange,
  confirmDeleteLead,
  filterPresets = [],
  isFilterPresetModalOpen,
  filterPresetName,
  filterPresetNameError,
  openSaveFilterPreset,
  closeSaveFilterPreset,
  setFilterPresetName,
  saveFilterPreset,
  applyFilterPreset,
  deleteFilterPreset,
}) {
  const { t } = useTranslation();

  return (
    <div>
      <FloatingAlerts error={error} />
      <div className="app-page-header d-flex align-items-start justify-content-between gap-3">
        <div>
          <h2>{t('leads.title')}</h2>
          <p>{t('leads.subtitle')}</p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
          {canImport && <Link to="/leads/import" className="btn btn-outline-primary">{t('leadImport.title')}</Link>}
          <Link
            to="/leads/new"
            className="btn btn-primary d-flex align-items-center"
          >
            <i className="ti ti-plus me-1 fs-5"></i>
            {t('leads.create')}
          </Link>
          <button
            type="button"
            className="btn btn-outline-secondary d-flex align-items-center"
            onClick={() => setIsFilterOpen((prev) => !prev)}
          >
            <i className="ti ti-adjustments-horizontal me-1 fs-5"></i>
            {t('profiles.filters')}
          </button>
        </div>
      </div>

      <div className="widget-content searchable-container list">
        {isFilterOpen ? (
          <div className="card card-body">
            <div className="row g-2">
              <div className="col-xl-2 col-md-4">
                <input
                  type="text"
                  name="firstName"
                  className="form-control"
                  placeholder={t('profiles.firstName')}
                  value={filters.firstName}
                  onChange={updateFilter}
                />
              </div>
              <div className="col-xl-2 col-md-4">
                <input
                  type="text"
                  name="lastName"
                  className="form-control"
                  placeholder={t('profiles.lastName')}
                  value={filters.lastName}
                  onChange={updateFilter}
                />
              </div>
              <div className="col-xl-2 col-md-4">
                <input
                  type="text"
                  name="email"
                  className="form-control"
                  placeholder={t('common.email')}
                  value={filters.email}
                  onChange={updateFilter}
                />
              </div>
              <div className="col-xl-2 col-md-4">
                <input
                  type="text"
                  name="phone"
                  className="form-control"
                  placeholder={t('leads.phone')}
                  value={filters.phone}
                  onChange={updateFilter}
                />
              </div>
              <div className="col-xl-2 col-md-4">
                <select
                  name="country"
                  className="form-control"
                  value={filters.country}
                  onChange={updateFilter}
                >
                  <option value="">{t('leads.anyCountry')}</option>
                  {countryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-xl-2 col-md-4">
                <input
                  type="text"
                  name="language"
                  className="form-control"
                  placeholder={t('leads.language')}
                  value={filters.language}
                  onChange={updateFilter}
                />
              </div>
              <div className="col-xl-2 col-md-4">
                <select name="status" className="form-control" value={filters.status} onChange={updateFilter}>
                  <option value="">{t('leads.anyStatus')}</option>
                  {renderOptions(LEAD_STATUS_OPTIONS, t, 'leadStatus')}
                </select>
              </div>
              <div className="col-xl-2 col-md-4">
                <select
                  name="collectionSource"
                  className="form-control"
                  value={filters.collectionSource}
                  onChange={updateFilter}
                >
                  <option value="">{t('leads.anyCollectionSource')}</option>
                  {renderOptions(LEAD_COLLECTION_SOURCE_OPTIONS, t, 'leadCollectionSource')}
                </select>
              </div>
              <div className="col-xl-2 col-md-4">
                <select
                  name="lawfulBasis"
                  className="form-control"
                  value={filters.lawfulBasis}
                  onChange={updateFilter}
                >
                  <option value="">{t('leads.anyLawfulBasis')}</option>
                  {renderOptions(LEAD_LAWFUL_BASIS_OPTIONS, t, 'leadLawfulBasis')}
                </select>
              </div>
              <div className="col-xl-2 col-md-4">
                <select
                  name="consentStatus"
                  className="form-control"
                  value={filters.consentStatus}
                  onChange={updateFilter}
                >
                  <option value="">{t('leads.anyConsentStatus')}</option>
                  {renderOptions(LEAD_CONSENT_STATUS_OPTIONS, t, 'leadConsentStatus')}
                </select>
              </div>
              <div className="col-xl-2 col-md-4">
                <select
                  name="privacyNoticeStatus"
                  className="form-control"
                  value={filters.privacyNoticeStatus}
                  onChange={updateFilter}
                >
                  <option value="">{t('leads.anyPrivacyNoticeStatus')}</option>
                  {renderOptions(LEAD_PRIVACY_NOTICE_STATUS_OPTIONS, t, 'leadPrivacyNoticeStatus')}
                </select>
              </div>
              <div className="col-xl-2 col-md-4">
                <div className="d-flex gap-2">
                  <FilterPresetSaveButton onClick={openSaveFilterPreset} className="flex-fill" />
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-fill"
                    aria-label={t('leads.clearFilters')}
                    onClick={resetAndCloseFilters}
                  >
                    <i className="ti ti-x"></i>
                  </button>
                </div>
              </div>
              <div className="col-12">
                <div className="d-flex align-items-center gap-4 flex-wrap">
                  <div className="form-check form-switch mb-0">
                    <input
                      id="leadsAssignedOnly"
                      type="checkbox"
                      name="assignedOnly"
                      className="form-check-input"
                      checked={filters.assignedOnly}
                      onChange={updateFilter}
                    />
                    <label htmlFor="leadsAssignedOnly" className="form-check-label">
                      {t('profiles.mineOnly')}
                    </label>
                  </div>
                  <div className="form-check form-switch mb-0">
                    <input
                      id="leadsMatchedOnly"
                      type="checkbox"
                      name="matchedOnly"
                      className="form-check-input"
                      checked={filters.matchedOnly}
                      onChange={updateFilter}
                    />
                    <label htmlFor="leadsMatchedOnly" className="form-check-label">
                      {t('leads.matchedOnly')}
                    </label>
                  </div>
                  <div className="form-check form-switch mb-0">
                    <input
                      id="leadsDoNotContact"
                      type="checkbox"
                      name="doNotContact"
                      className="form-check-input"
                      checked={filters.doNotContact}
                      onChange={updateFilter}
                    />
                    <label htmlFor="leadsDoNotContact" className="form-check-label">
                      {t('leads.doNotContact')}
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <FilterPresetList presets={filterPresets} onApply={applyFilterPreset} onDelete={deleteFilterPreset} />
          </div>
        ) : null}

        <div className={`card card-body${isFilterOpen ? ' mt-3' : ''}`}>
          <div className="table-responsive">
            <table className="table search-table align-middle text-nowrap mb-0">
              <thead className="header-item">
                <tr>
                  <th>
                    <button type="button" className="app-sort-header" onClick={() => toggleSort(LEAD_SORTABLE_COLUMNS.firstName)}>
                      {t('common.name')} <i className={`${sortIndicator(LEAD_SORTABLE_COLUMNS.firstName)} app-sort-icon`}></i>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="app-sort-header" onClick={() => toggleSort(LEAD_SORTABLE_COLUMNS.email)}>
                      {t('common.email')} <i className={`${sortIndicator(LEAD_SORTABLE_COLUMNS.email)} app-sort-icon`}></i>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="app-sort-header" onClick={() => toggleSort(LEAD_SORTABLE_COLUMNS.phone)}>
                      {t('leads.phone')} <i className={`${sortIndicator(LEAD_SORTABLE_COLUMNS.phone)} app-sort-icon`}></i>
                    </button>
                  </th>
                  <th>
                    <button type="button" className="app-sort-header" onClick={() => toggleSort(LEAD_SORTABLE_COLUMNS.country)}>
                      {t('profiles.country')} <i className={`${sortIndicator(LEAD_SORTABLE_COLUMNS.country)} app-sort-icon`}></i>
                    </button>
                  </th>
                  <th>{t('common.status')}</th>
                  <th>{t('leads.collectionSource')}</th>
                  <th>{t('leads.lawfulBasis')}</th>
                  <th>{t('leads.consentStatus')}</th>
                  <th>{t('leads.privacyNoticeStatus')}</th>
                  <th>{t('profiles.assignedTo')}</th>
                  <th>{t('leads.matchedProfile')}</th>
                  <th>
                    <button type="button" className="app-sort-header" onClick={() => toggleSort(LEAD_SORTABLE_COLUMNS.created)}>
                      {t('profiles.created')} <i className={`${sortIndicator(LEAD_SORTABLE_COLUMNS.created)} app-sort-icon`}></i>
                    </button>
                  </th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>

              <tbody>
                {isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="text-center text-muted py-4">{t('common.loading')}</td>
                  </tr>
                ) : null}

                {!isLoading && rows.length === 0 ? (
                  <tr>
                    <td colSpan="13" className="text-center text-muted py-4">{t('leads.noLeads')}</td>
                  </tr>
                ) : null}

                {rows.map((row) => {
                  const matchedProfilePath = buildMatchedProfilePath(row);
                  const matchedProfileLabel = getMatchedProfileLabel(row);

                  return (
                    <tr key={row.key} className="search-items">
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <h6 className="user-name mb-0">{row.name}</h6>
                          {row.doNotContact ? (
                            <span className="badge rounded-pill fw-semibold bg-danger-subtle text-danger">
                              {t('leads.doNotContact')}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td>{row.email}</td>
                      <td>{row.phone}</td>
                      <td>{row.country}</td>
                      <td>
                        <span className={`badge rounded-pill fw-semibold text-uppercase ${getStatusBadgeClass(row.status)}`}>
                          {row.status || '-'}
                        </span>
                      </td>
                      <td>{row.collectionSource || '-'}</td>
                      <td>
                        <span className={`badge rounded-pill fw-semibold text-uppercase ${getGdprBadgeClass('lawfulBasis', row.lawfulBasis)}`}>
                          {row.lawfulBasis || '-'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge rounded-pill fw-semibold text-uppercase ${getGdprBadgeClass('consentStatus', row.consentStatus)}`}>
                          {row.consentStatus || '-'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge rounded-pill fw-semibold text-uppercase ${getGdprBadgeClass('privacyNoticeStatus', row.privacyNoticeStatus)}`}>
                          {row.privacyNoticeStatus || '-'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`btn btn-sm ${row.assignedToName && row.assignedToName !== '-' ? 'btn-outline-primary' : 'btn-primary'}`}
                          onClick={() => openAssignModal(row)}
                          disabled={!row.id}
                        >
                          {row.assignedToName && row.assignedToName !== '-' ? row.assignedToName : t('common.assign')}
                        </button>
                      </td>
                      <td>
                        {row.isMatchedProfile && matchedProfilePath ? (
                          <Link to={matchedProfilePath} className="d-inline-flex align-items-center gap-1">
                            <span>{matchedProfileLabel || t('leads.matchedProfile')}</span>
                            <i className="ti ti-external-link"></i>
                          </Link>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{row.createdAt}</td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            aria-label={t('common.assign')}
                            title={t('common.assign')}
                            onClick={() => openAssignModal(row)}
                            disabled={!row.id}
                          >
                            <i className="ti ti-user-plus"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            aria-label={t('common.edit')}
                            title={t('common.edit')}
                            onClick={() => openLeadForm(row)}
                          >
                            <i className="ti ti-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            aria-label={t('common.delete')}
                            title={t('common.delete')}
                            onClick={() => openDeleteModal(row)}
                          >
                            <i className="ti ti-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {hasMoreLeads && !isLoading ? (
            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={loadMoreLeads}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? t('common.loading') : t('leads.loadMore')}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <AssignmentModal
        isOpen={isAssignModalOpen}
        title={t('leads.assignLeadManager')}
        targetLabel={t('leads.lead')}
        targetName={assignTargetLeadName}
        error={assignmentError}
        options={adminProfileOptions}
        isOptionsLoading={isAdminProfilesLoading}
        selectedId={selectedManagerId}
        isSaving={isAssignmentSaving}
        showUnassign
        onClose={closeAssignModal}
        onSelect={onSelectManager}
        onAssign={assignManager}
        onUnassign={unassignManager}
      />
      <FilterPresetNameModal
        isOpen={isFilterPresetModalOpen}
        name={filterPresetName}
        error={filterPresetNameError}
        onNameChange={setFilterPresetName}
        onSubmit={saveFilterPreset}
        onClose={closeSaveFilterPreset}
      />
      <LeadFormModal
        isOpen={isLeadFormOpen}
        target={leadFormTarget}
        form={leadForm}
        formErrors={leadFormErrors}
        error={leadFormError}
        isSaving={isLeadFormSaving}
        isLoadingDetails={isLeadFormLoadingDetails}
        countryOptions={countryOptions}
        onChange={onLeadFormChange}
        onAddDetail={addLeadDetailRow}
        onRemoveDetail={removeLeadDetailRow}
        onSubmit={saveLeadForm}
        onClose={closeLeadForm}
      />
      <LeadDeleteModal
        isOpen={isDeleteModalOpen}
        target={deleteTargetLead}
        reason={deleteReason}
        error={deleteError}
        isDeleting={isDeletingLead}
        onReasonChange={onDeleteReasonChange}
        onConfirm={confirmDeleteLead}
        onClose={closeDeleteModal}
      />
    </div>
  );
}

export default LeadsPageView;
